import { useState, useEffect } from "react";
import DataTable from "../../utils/components/DataTable";
import Breadcrumbs from "../../utils/components/Breadcrumbs";
import Button from "../../utils/components/Button";
import Input from "../../utils/components/Input";
import Modal from "../../utils/components/Modal";
import Loading from "../../utils/components/Loading";
import { useProject } from "../../utils/hooks/useProject";
import {
  AiOutlinePlusCircle,
  AiOutlineExport,
  AiOutlineSearch,
} from "react-icons/ai";
import { IoMdClose } from "react-icons/io";
import { BiFilterAlt } from "react-icons/bi";
import { useNavigate } from "react-router";
import { auth } from "../../App/routes";
import API from "../../utils/API";
import { Translate } from "react-localize-redux";
import timestampToDateFormat from "../../utils/helpers/timestampToDateFormat";
import { useCan } from "../../utils/hooks/useCan";

const SEARCH_TIMEOUT = 800;

const FormTemplates = ({ translate }) => {
  const [show, setShow] = useState(false);
  const [formTemplateList, setFormTemplateList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(
    localStorage.getItem("form-templates-per-page")
      ? parseInt(localStorage.getItem("form-templates-per-page"))
      : 10
  );
  const [attributes, setAttributes] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [tempSelectedAttributes, setTempSelectedAttributes] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [query, setQuery] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [searchedQuery, setSearchedQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isProjectControl, setIsProjectControl] = useState(true);

  const { project } = useProject();
  const navigate = useNavigate();
  const can = useCan();

  const [columns] = useState([
    {
      name: translate("form_templates.uppercase_translates.form_name"),
      selector: (row) => row.name,
      sortable: true,
      minWidth: "400px",
      cell: (row) => (
        <div className="row-status" data-status={row.status}>
          {row.name}
        </div>
      ),
    },
    {
      name: translate("form_templates.uppercase_translates.created_at"),
      selector: (row) => row.created_at,
      format: (row) => timestampToDateFormat(row.created_at),
      sortable: true,
    },
    {
      name: translate("form_templates.uppercase_translates.modified_at"),
      selector: (row) => row.modified_at,
      format: (row) => timestampToDateFormat(row.modified_at),
      sortable: true,
    },
    {
      name: translate("form_templates.uppercase_translates.version"),
      selector: (row) => row.version,
      sortable: true,
    },
    {
      name: translate("form_templates.uppercase_translates.template_state"),
      selector: (row) => row.template_state,
      sortable: true,
      center: true,
      conditionalCellStyles: [
        {
          when: (row) => row.status === 1,
          classNames: ["fw-bold", "text-uppercase", "col-status--inactive"],
        },
        {
          when: (row) => row.status === 2,
          classNames: ["fw-bold", "text-uppercase", "col-status--published"],
        },
        {
          when: (row) => row.status === 3,
          classNames: ["fw-bold", "text-uppercase", "col-status--draft"],
        },
      ],
    },
  ]);

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setResetPaginationToggle(!resetPaginationToggle);
    getFormTemplates(newPerPage, page);
    localStorage.setItem("form-templates-per-page", newPerPage);
  };

  const handlePageChange = (page) => {
    getFormTemplates(perPage, page);
  };

  const handleSearch = (e) => {
    const query = e.target.value;

    if (project) {
      setIsLoading(true);
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (query === searchedQuery) {
      setIsLoading(false);
      return;
    }

    setSearchTimeout(
      setTimeout(() => {
        setQuery(query);
      }, SEARCH_TIMEOUT)
    );
  };

  const removeSelectedAttribute = (id) => {
    setSelectedAttributes(selectedAttributes.filter((item) => item.id !== id));
    setTempSelectedAttributes(
      tempSelectedAttributes.filter((item) => item.id !== id)
    );
  };

  const selectedAttributesControl = (id) => {
    return tempSelectedAttributes.find((item) => item.id === id);
  };

  const handleAttributesClick = (detail) => {
    const isSelected = selectedAttributesControl(detail.id);
    let updatedSelectedAttributes = [];
    if (isSelected) {
      updatedSelectedAttributes = tempSelectedAttributes.filter(
        (t) => t.id !== detail.id
      );
    } else {
      updatedSelectedAttributes = [...tempSelectedAttributes, detail];
    }

    setTempSelectedAttributes(updatedSelectedAttributes);
  };

  const handleModalApply = () => {
    setSelectedAttributes(tempSelectedAttributes);
    setShow(false);
  };

  const determineStatus = (templateState) => {
    switch (templateState) {
      case "draft":
        return 3;
      case "published":
        return 2;
      case "inactive":
        return 1;
      default:
        return 0;
    }
  };

  const getFormTemplates = (newPerPage, page) => {
    setIsLoading(true);
    const data = {
      attributes: selectedAttributes?.map((item) => item.id),
    };
    API.getFormTemplates(project?.id, page, newPerPage, query, false, data)
      .then(({ data: res }) => {
        setSearchedQuery(query);

        let tableList = res.data.map((item) => {
          let template_state = item.status.split(".")[1];
          let status = determineStatus(template_state);
          let modified_at = item.modified_at || null;
          let created_at = item.created_at || null;

          return {
            id: item.id,
            name: item.name,
            description: item.description,
            created_at,
            modified_at,
            status: status,
            version: item.version,
            template_state: translate(
              `form_templates.uppercase_translates.statuses.${template_state}`
            ),
          };
        });
        setFormTemplateList(tableList);
        setTotalRows(res.pagination.total_count);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const getAttributes = () => {
    API.getData(`/metadata_sets?project_id=${project.id}`).then(
      ({ data: res }) => {
        setAttributes(res.data);
      }
    );
  };

  useEffect(() => {
    if (project) {
      getFormTemplates(perPage, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAttributes, query]);

  useEffect(() => {
    if (!show) {
      setTempSelectedAttributes(selectedAttributes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (project) {
      setIsProjectControl(false);
      getAttributes();
    } else {
      setIsLoading(false);
      setIsProjectControl(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container-fluid">
      <div className="row mt-4 mb-3">
        <div className="col-12 col-md-6">
          <Breadcrumbs
            data={[
              {
                id: 0,
                customTitle: project?.name || (
                  <Translate id={auth.projects.name} />
                ),
                route: "auth.projects",
              },
              { id: 1, title: "page_titles.form-templates" },
            ]}
          />
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-end">
          <Button className="btn-secondary mx-2">
            <div className="d-flex align-items-center">
              <AiOutlineExport className="fs-5 me-2" />
              <Translate id="export_as" />
            </div>
          </Button>
          <Button className="btn-secondary" onClick={() => setShow(true)}>
            <div className="d-flex align-items-center">
              <BiFilterAlt className="fs-5 me-2" />
              <Translate id="filter" />
            </div>
          </Button>
          {can.doThat("formTemplate:create") && (
            <Translate>
              {({ translate }) => (
                <Button
                  className="btn-primary ms-2"
                  onClick={() =>
                    project?.id
                      ? navigate(auth.newFormTemplate.link)
                      : alert(translate("select_a_project_alert"))
                  }
                >
                  <div className="d-flex align-items-center">
                    <AiOutlinePlusCircle className="fs-4 me-2" />
                    {translate("form_templates.new_form_template")}
                  </div>
                </Button>
              )}
            </Translate>
          )}
        </div>
      </div>
      <div className="row mb-2">
        <div className="col-12 col-md-7 m-2">
          {selectedAttributes?.map((attribute, index) => (
            <span
              className="badge bg-secondary text-center py-1 mt-1 me-3"
              key={index}
            >
              <IoMdClose
                className="fw-bold cursor--pointer fill-white fs-5"
                onClick={() => removeSelectedAttribute(attribute.id)}
              />
              {attribute.name}
            </span>
          ))}
        </div>
        <div className="col-12 col-md-4 d-flex align-items-center ms-auto">
          <p className="mb-0 me-2">
            <Translate id="search" />:
          </p>
          <Input
            placeholder={translate(
              "form_templates.placeholders.enter-filter-content"
            )}
            onChange={handleSearch}
            iconNode={<AiOutlineSearch className="fs-5 me-2 mt-2" />}
          />
        </div>
      </div>
      <div className="row">
        <DataTable
          columns={columns}
          data={formTemplateList}
          onRowClicked={(row) => {
            if (row.id) {
              navigate(auth.formTemplateDetails.link.replace(":id", row.id));
            }
          }}
          progressComponent={
            <Loading classNames="mt-3" text={translate("loading") + `...`} />
          }
          progressPending={isLoading}
          pointerOnHover
          conditionalRowStyles={[
            {
              when: (row) => row.status === 1,
              classNames: ["row-status", "row-status--inactive"],
            },
            {
              when: (row) => row.status === 2,
              classNames: ["row-status", "row-status--published"],
            },
            {
              when: (row) => row.status === 3,
              classNames: ["row-status", "row-status--draft"],
            },
          ]}
          pagination
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          paginationResetDefaultPage={resetPaginationToggle}
        />
      </div>

      <Modal
        title={translate("alert")}
        modaSize="lg"
        show={isProjectControl && !isLoading}
        setShow={setIsProjectControl}
      >
        <p className="fs-5 text-center mt-2">
          {translate("select_a_project_alert")}
        </p>
      </Modal>
      <Modal
        title={translate("filter")}
        positiveButtonNode={translate("apply")}
        negativeButtonNode={translate("cancel")}
        positiveButtonHandler={() => handleModalApply()}
        modalSize="lg"
        show={show}
        setShow={setShow}
      >
        <div>
          {attributes?.map((item, index) => (
            <div className="row mb-3" key={index}>
              <div className="col-12 text-center">
                <h3 className="fw-bold text-uppercase">{item.name}</h3>
              </div>
              <div className="col-12 mb-3 text-center">
                {item.details.map((detail, index2) => (
                  <Button
                    className={`me-2 mb-2 ${
                      selectedAttributesControl(detail.id) ? "btn-success" : ""
                    }`}
                    onClick={() => handleAttributesClick(detail)}
                    key={index2}
                  >
                    {detail.name}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default FormTemplates;
